import { Fragment } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { drilledPath } from '@/features/ogsm/search'
import { useActiveOgsmBoard } from '@/hooks/use-ogsm'

/**
 * The breadcrumb. Drilled into a node at any depth, it lists that node's
 * ancestors as links and ends at the node, taken from the board already loaded
 * for the page. The title row is the workspace's.
 */
export function OgsmPageHeader({
  companyId,
  companyName,
  sessionId,
  objectiveId,
}: {
  companyId: string
  companyName: string
  sessionId?: string
  objectiveId?: string
}) {
  const { board } = useActiveOgsmBoard(companyId, sessionId)
  // A stale id matches nothing, and the board then shows the root.
  const path = board.data ? drilledPath(board.data, objectiveId) : []
  const drilled = path.at(-1)
  const ancestors = path.slice(0, -1)

  return (
    <nav className="flex min-w-0 flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
      <Link
        to="/companies"
        className="flex items-center gap-2 text-primary hover:text-primary/80"
      >
        <ArrowLeft className="size-4" />
        <Typography as="span" variant="body-sm">
          Companies
        </Typography>
      </Link>
      <ChevronRight className="size-3.5 text-primary" />
      <Link
        to="/companies/$companyId"
        params={{ companyId }}
        className="min-w-0 truncate text-primary hover:text-primary/80"
      >
        <Typography as="span" variant="body-sm">
          {companyName}
        </Typography>
      </Link>
      <ChevronRight className="size-3.5 text-primary" />
      {drilled ? (
        <>
          <Link
            to="/companies/$companyId/ogsm"
            params={{ companyId }}
            search={{ session: sessionId }}
            className="text-primary hover:text-primary/80"
          >
            <Typography as="span" variant="body-sm">
              {OGSM_COPY.title}
            </Typography>
          </Link>
          <ChevronRight className="size-3.5 text-primary" />
          {ancestors.map((ancestor) => (
            <Fragment key={ancestor.id}>
              <Link
                to="/companies/$companyId/ogsm"
                params={{ companyId }}
                search={{ session: sessionId, objective: ancestor.id }}
                className="min-w-0 text-primary hover:text-primary/80"
              >
                <Typography
                  as="span"
                  variant="body-sm"
                  title={ancestor.title}
                  className="block min-w-0 max-w-40 truncate"
                >
                  {ancestor.title}
                </Typography>
              </Link>
              <ChevronRight className="size-3.5 shrink-0 text-primary" />
            </Fragment>
          ))}
          <Typography
            as="span"
            variant="body-sm"
            title={drilled.title}
            className="min-w-0 max-w-64 truncate font-semibold text-primary sm:max-w-96"
          >
            {drilled.title}
          </Typography>
        </>
      ) : (
        <Typography
          as="span"
          variant="body-sm"
          className="font-semibold text-primary"
        >
          {OGSM_COPY.title}
        </Typography>
      )}
    </nav>
  )
}
